import { Connection, Transaction } from '@solana/web3.js';
import bs58 from 'bs58';

// Deliberately NOT reusing utils/utils.ts's own `confirmTransaction`:
// that module also exports browser-only constants (`isExtension` reads
// `window.location` at import time), which makes importing it fail
// outright in a plain Node environment — exactly what @solana/web3.js's
// own crypto needs for this module's tests (see walletBridge.test.ts's
// `@jest-environment node` docblock). Inlining this handful of lines
// avoids that fragile transitive dependency instead of touching a
// shared, widely-used file's unrelated exports just for this.
async function confirmTransaction(connection: Connection, signature: string): Promise<void> {
  const latestBlockhash = await connection.getLatestBlockhash();
  const result = await connection.confirmTransaction(
    {
      blockhash: latestBlockhash.blockhash,
      lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
      signature,
    },
    'finalized',
  );
  if (result.value.err) {
    throw new Error('Error confirming transaction: ' + JSON.stringify(result.value.err));
  }
}

/** Must match `backup.BACKUP_KEY_MESSAGE` in the edge node's backup.py
 * exactly — this fixed string is what the wallet signs so the resulting
 * (deterministic, since Ed25519 signatures are deterministic per
 * RFC 8032) signature can be hashed into the same backup encryption key
 * on any device where this wallet is restored. See
 * docs/backup-protocol.md. */
export const BACKUP_KEY_MESSAGE = 'domihealth-backup-key-v1';

/** Just the surface of `utils/wallet.js`'s `Wallet` this module needs —
 * matches its real `signTransaction`/`createSignature` signatures
 * exactly (both declared `async` there, so always resolve a Promise
 * even though e.g. `LocalStorageWalletProvider.createSignature` is
 * itself synchronous underneath). */
export interface SigningWallet {
  signTransaction(transaction: Transaction): Promise<Transaction>;
  createSignature(message: Uint8Array | Buffer): Promise<string>; // bs58-encoded, per every WalletProvider in this codebase
}

export function deserializeTransaction(base64Transaction: string): Transaction {
  return Transaction.from(Buffer.from(base64Transaction, 'base64'));
}

/** Adds the user wallet's signature to a transaction the edge node
 * already built and device-signed (`chain.py`'s `PendingClaim`), then
 * submits it.
 *
 * Deliberately does NOT reuse this codebase's existing
 * `signAndSendTransaction` (utils/tokens/index.js): that helper
 * overwrites `recentBlockhash` and calls `transaction.setSigners(...)`,
 * which rebuilds the signature array from scratch — exactly wrong here,
 * since it would silently discard the device's already-present
 * signature. `wallet.signTransaction`'s underlying `partialSign` is
 * additive instead: it fills in this wallet's own empty signature slot
 * (matched by public key against the message's existing account keys)
 * without touching the device's signature or the blockhash the device
 * signed over.
 */
export async function coSignAndSubmit(
  connection: Connection,
  wallet: SigningWallet,
  base64Transaction: string,
): Promise<string> {
  const transaction = deserializeTransaction(base64Transaction);
  const signed = await wallet.signTransaction(transaction);
  const signature = await connection.sendRawTransaction(signed.serialize(), {
    preflightCommitment: 'single',
  });
  await confirmTransaction(connection, signature);
  return signature;
}

/** Derives the hex-encoded signature `service.py`'s `wallet_signature`
 * field expects for backup creation/restore (see
 * docs/backup-protocol.md: `backup_key = SHA256(wallet.sign(...))` — the
 * edge node does the hashing; this only has to produce the raw
 * signature bytes, hex-encoded, from what every `WalletProvider` in this
 * codebase already returns bs58-encoded). */
export async function deriveBackupSignatureHex(wallet: SigningWallet): Promise<string> {
  const signatureBs58 = await wallet.createSignature(Buffer.from(BACKUP_KEY_MESSAGE));
  return Buffer.from(bs58.decode(signatureBs58)).toString('hex');
}

/** A message distinct from `BACKUP_KEY_MESSAGE` — deliberately not
 * reusing that one for this too, so the DB-unlock secret and the
 * backup-encryption secret stay cryptographically independent: exposing
 * one (e.g. a remote-pinning provider seeing a backup ciphertext's key)
 * must never also expose the other. */
export const UNLOCK_PASSPHRASE_MESSAGE = 'domihealth-unlock-passphrase-v1';

/** Deterministically derives this wallet's DomiHealth unlock passphrase
 * — same Ed25519-determinism reasoning as `deriveBackupSignatureHex`,
 * so the user never has to invent, remember, or type a separate "local
 * passphrase" the wallet doesn't already manage. `storage.open_user_db`
 * on the edge node treats whatever passphrase it's given as an opaque
 * string (PBKDF2 input), so no edge-node change is needed — this is a
 * client-only change to what gets sent as that string. */
export async function deriveUnlockPassphrase(wallet: SigningWallet): Promise<string> {
  const signatureBs58 = await wallet.createSignature(Buffer.from(UNLOCK_PASSPHRASE_MESSAGE));
  return Buffer.from(bs58.decode(signatureBs58)).toString('hex');
}
